import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { filter, map, take } from "rxjs";

import { DocumentDetails } from "@app/constants/document-mappings";
import { getLanguageLocaleCulture } from "@app/constants/language";
import { DOCUMENT_TYPE } from "@app/models/registration-form";
import { AppStateService } from "@app/services/app-state.service";
import { RouteService } from "@app/services/route.service";
import { CustomValidators } from "@app/shared/validators/custom-validators";

@Component({
  selector: "app-commissioning-electricity-contact",
  templateUrl: "./commissioning-electricity-contact.component.html",
  standalone: false,
})
export class CommissioningElectricityContactComponent implements OnInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #formBuilder: FormBuilder = inject(FormBuilder);
  readonly #appStateService: AppStateService = inject(AppStateService);
  readonly #routeService: RouteService = inject(RouteService);
  readonly #translateService = inject(TranslateService);
  readonly #currentDate: Date = new Date();

  public contactForm!: FormGroup;
  public allTouched = false;
  public powerOfAttorneyDocumentDetails: DocumentDetails = {
    typeName: DOCUMENT_TYPE.POWER_OF_ATTORNEY,
  };
  public currentLanguageCulture = getLanguageLocaleCulture(
    this.#translateService.currentLang,
  );

  public ngOnInit(): void {
    this.#createContactForm();
    this.#updateFormFromState();
    this.#watchDifferentAddress();
  }

  #createContactForm(): void {
    this.contactForm = this.#formBuilder.group({
      requesterDetails: this.#formBuilder.group({
        basicData: this.#formBuilder.group(
          {
            firstName: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                Validators.required,
              ],
            ],
            lastName: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                Validators.required,
              ],
            ],
            email: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                CustomValidators.email,
                Validators.required,
              ],
            ],
            phone: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                CustomValidators.phoneNumberValidator(),
                Validators.required,
              ],
            ],
          },
          { updateOn: "blur" },
        ),
        hasDifferentAddress: [null, Validators.required],
        differentAddress: this.#formBuilder.group({
          zipCode: [
            null,
            [
              CustomValidators.trimValidator,
              CustomValidators.shortText,
              Validators.required,
              CustomValidators.germanZip,
            ],
          ],
          city: [
            null,
            [
              CustomValidators.trimValidator,
              CustomValidators.shortText,
              Validators.required,
            ],
          ],
          street: [
            null,
            [
              CustomValidators.trimValidator,
              CustomValidators.shortText,
              Validators.required,
            ],
          ],
          streetNumber: [
            null,
            [
              CustomValidators.trimValidator,
              CustomValidators.shortText,
              Validators.required,
            ],
          ],
        }),
      }),
      electricianDetails: this.#formBuilder.group({
        basicData: this.#formBuilder.group(
          {
            firstName: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                Validators.required,
              ],
            ],
            lastName: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                Validators.required,
              ],
            ],
            email: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                CustomValidators.email,
                Validators.required,
              ],
            ],
            phone: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                CustomValidators.phoneNumberValidator(),
                Validators.required,
              ],
            ],
          },
          { updateOn: "blur" },
        ),
        gridOperator: [
          null,
          {
            updateOn: "blur",
            validators: [
              CustomValidators.trimValidator,
              CustomValidators.shortText,
              Validators.required,
            ],
          },
        ],
        registrationNumber: [
          null,
          {
            updateOn: "blur",
            validators: [
              CustomValidators.trimValidator,
              CustomValidators.shortText,
              Validators.required,
            ],
          },
        ],
        companyName: [
          null,
          {
            updateOn: "blur",
            validators: [
              CustomValidators.trimValidator,
              CustomValidators.shortText,
              Validators.required,
            ],
          },
        ],
        address: this.#formBuilder.group(
          {
            zipCode: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                Validators.required,
                CustomValidators.germanZip,
              ],
            ],
            city: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                Validators.required,
              ],
            ],
            street: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                Validators.required,
              ],
            ],
            streetNumber: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                Validators.required,
              ],
            ],
          },
          { updateOn: "blur" },
        ),
        date: this.#currentDate,
        powerOfAttorney: [null, Validators.requiredTrue],
        powerOfAttorneyDocument: [null],
      }),
    });
  }

  #updateFormFromState(): void {
    this.#appStateService
      .observeState()
      .pipe(
        map(({ formData }) => formData.commissioningElectricityContact),
        filter(Boolean),
        take(1),
      )
      .subscribe((contactData) => {
        this.contactForm.patchValue(contactData);
        this.#evaluateDifferentAddress();
      });
  }

  #evaluateDifferentAddress(): void {
    if (this.contactForm.get("requesterDetails.hasDifferentAddress")!.value) {
      this.contactForm.get("requesterDetails.differentAddress")!.enable();
    } else {
      this.contactForm.get("requesterDetails.differentAddress")!.disable();
    }
  }

  #watchDifferentAddress(): void {
    this.contactForm
      .get("requesterDetails.hasDifferentAddress")!
      .valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#evaluateDifferentAddress());
  }

  #updateState(): void {
    this.#appStateService.updateFormData({
      commissioningElectricityContact: this.contactForm.value,
    });
  }

  public previous(): void {
    this.#updateState();
    this.#routeService.navigateToPreviousStep();
  }

  public next(): void {
    if (this.contactForm.valid) {
      this.#updateState();
      this.#routeService.navigateToNextStep();
    } else {
      this.contactForm.markAllAsTouched();
      this.allTouched = true;
    }
  }
}
